import { usePlayer, usePlayerStoreApi } from "@utils/player/core/usePlayer";
import { useEffect, useMemo, useRef } from "react";
import { _spacing, _slideWidth } from "./parallax";
import {
  useAnimatedScrollHandler,
  useSharedValue,
} from "react-native-reanimated";

import { useListUnlockedPrivateSongs } from "@hooks/trpc/useListUnlockedPrivateSongs";
import { useData } from "../../../../runtime/provider";
import { artistProfileQuery } from "@hooks/trpc/useArtists";
import {
  artistAccessCarouselQuery,
  useArtistAccessCarousel,
} from "@hooks/trpc/useArtistAccessCarousel";

import {
  buildAccessCarouselData,
  resolveActiveSongForAccessCarousel,
} from "./buildAccessCarouselData";

export const useAccessCarouselData = (artistId: string) => {
  const playerStore = usePlayerStoreApi();

  const dataSession = useData();
  const { queryClient } = dataSession;
  const {
    query: { data: carouselData, isFetched },
  } = useArtistAccessCarousel(artistId);

  const scrollX = useSharedValue(0);
  const onScroll = useAnimatedScrollHandler((e) => {
    scrollX.value = e.contentOffset.x / (_slideWidth + _spacing);
  });

  const unlockedSongIds = useListUnlockedPrivateSongs();
  const activeSong = usePlayer((state) => {
    const activeQueue =
      state.activeQueueType === "foryou"
        ? state.foryouQueue
        : state.playlistQueue;
    const index = activeQueue?.currentIndex ?? -1;
    return index >= 0 ? activeQueue?.items.at(index)?.metadata ?? null : null;
  });
  const resolvedActiveSong = useMemo(
    () => resolveActiveSongForAccessCarousel(activeSong, dataSession.db),
    [activeSong, dataSession],
  );

  // Prefetch artist profile and next song's access data
  // Read from store directly to avoid subscription re-renders
  // PERF: Use refs to avoid redundant prefetching
  const lastPrefetchedArtistRef = useRef<string | null>(null);
  const lastPrefetchedNextArtistRef = useRef<string | null>(null);

  useEffect(() => {
    if (!artistId) return;

    // Skip if already prefetched this artist
    if (lastPrefetchedArtistRef.current !== artistId) {
      lastPrefetchedArtistRef.current = artistId;
      queryClient
        .prefetchQuery(artistProfileQuery(dataSession, artistId))
        .catch((error) => {
          console.error(
            "::useAccessCarouselData:: Failed to prefetch artist profile",
            artistId,
            error,
          );
        });
    }

    // Read queue state directly from store - no subscription needed for prefetching
    const store = playerStore.getState();
    const activeQueue = store.foryouQueue;
    if (!activeQueue) return;

    const items = activeQueue.items;
    const activeIndex = activeQueue.currentIndex;
    const nextSong = activeIndex >= 0 ? items.at(activeIndex + 1) : null;
    const nextArtistId = nextSong?.metadata.artists[0].id ?? null;

    // Skip if already prefetched this next artist
    if (
      activeIndex !== -1 &&
      nextArtistId &&
      lastPrefetchedNextArtistRef.current !== nextArtistId
    ) {
      lastPrefetchedNextArtistRef.current = nextArtistId;
      queryClient
        .prefetchQuery(artistAccessCarouselQuery(dataSession, nextArtistId))
        .catch((error) => {
          console.error(
            "::useAccessCarouselData:: Failed to prefetch next artist carousel",
            nextArtistId,
            error,
          );
        });
    }
  }, [artistId, dataSession, queryClient]);

  const data = useMemo(
    () =>
      buildAccessCarouselData({
        artistId,
        carouselData,
        isCarouselFetched: isFetched,
        activeSong: resolvedActiveSong,
        unlockedSongIds,
      }),
    [artistId, carouselData, isFetched, resolvedActiveSong, unlockedSongIds],
  );

  return {
    data,
    onScroll,
    scrollX,
  };
};
